import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { unitId as brandUnitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Field } from '@/components/Field'
import { GridTable } from '@/components/GridTable'
import { getApiClient, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { WizardProse, WizardButtons, WizardSplit, WizardCard, WizardHeading } from '@/components/WizardParts'
import { DestinationWorkspacePicker } from './DestinationWorkspacePicker'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import type { Problem } from '@/data/apiClient'
import type { RestoreWizardConfig, RestoreObjectCatalogue } from '@/layers/restore-wizard/types'

/**
 * `repo-wz-2`/`access-wz-2`/`project-wz-2`/`ci-wz-2`/`settings-wz-2` — the `destination` role's
 * shared pane: "**Where does it go, and how much of it?**" Object types come from
 * `organisationGetRestoreObjectCatalogue` (already fetched once for `v-restore`'s launcher, W145;
 * refetched here since a wizard entered directly carries no cache from that screen). A
 * **non-restorable object type is shown with its reason here, before the machine is entered
 * further** (§18.3b) — `fidelity` is the sentence, `not_restorable` is the flag, and they must
 * agree by construction since both come from the same served row.
 *
 * **The "Requires" line is unconditional** — it used to hide entirely when `prerequisite` was the
 * literal string `'None'`, which read as a missing field rather than as "this one has none"; the
 * server's own value already says which, so this just renders it.
 *
 * `ref` reuses this pane only for its destination-workspace field — a single-ref restore's object
 * list IS the one ref (its type and its instance are the same thing, per `RestoreInput`'s own
 * description), so the object-type table is skipped entirely when `config.singleRef`.
 *
 * **The checklist became a real `data-cmp="grid-table"` W163, gated on the catalogue's own object
 * count rather than on `config.kind`.** `repo-wz-2`'s own spec says "No table on this pane" in
 * words — its catalogue entry (`REPOSITORY`) has exactly one object type, and a one-row checklist
 * would let an operator uncheck the only thing this machine ever restores, which is not a real
 * choice. The same `objects.length > 1` gate that skips the table for `repo` also covers any other
 * kind whose catalogue ever narrows to one type, without hardcoding which kind that is. A real
 * "Refresh" `icon-button` (`grid-tools`) and a `Chip` for each row's own `fidelity` were added to
 * the table; a new "Recovering" `Field` (a real `chip in field-control`, the spec's own words for
 * every one of these six units) summarises the selection count, or `config.kindLabel` for the
 * `singleRef` case, which has nothing else this pane already knows to name it by.
 *
 * **The fidelity chip recoloured (W172)** — was `not_restorable ? 'off' : 'plain'`, always
 * colourless. The prototype's own fidelity column (`ref-wz-2`'s and `access-wz-1`'s "Choose what
 * to restore" tables) draws `Full` as `st-active`, every partial-fidelity reason (`Exemptions may
 * drop`, `Secrets not captured`, `Not restorable`…) as `solid-warn`, and a genuinely blocked row
 * (`blocked by auth`) as `solid-fail` — matched here on `fidelity === 'Full'` and `not_restorable`,
 * the same two fields the prototype's own rows are keyed on.
 */
export function TargetObjectTypesPane({
  config,
  heading,
  destinationWorkspaceName,
  onNext,
  onBack,
}: {
  config: RestoreWizardConfig
  heading: string
  destinationWorkspaceName: string
  onNext: (args: { objectTypes?: readonly string[]; destinationWorkspaceId?: string; destinationLabel: string }) => void
  onBack: () => void
}) {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()
  const [checked, setChecked] = useState<ReadonlySet<string> | undefined>(undefined)
  const [destination, setDestination] = useState<{ id: string; name: string }>({ id: workspaceId, name: destinationWorkspaceName })

  const catalogue = useQuery({
    queryKey: ['restore-object-catalogue', orgId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId),
    queryFn: async (): Promise<RestoreObjectCatalogue> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/reference/restore-objects', { params: { path: { org_id: orgId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as RestoreObjectCatalogue
    },
  })

  const kindEntry = catalogue.data?.kinds.find((k) => k.kind === config.kind)
  const restorableObjects = kindEntry?.objects.filter((o) => !o.not_restorable) ?? []
  // Every restorable object type starts selected — the operator narrows, rather than building the
  // set up from nothing — computed in render rather than seeded via an effect (no cascading
  // render, and it stays correct if the catalogue resolves after the first paint).
  const effectiveChecked = checked ?? new Set(restorableObjects.map((o) => o.object_type))

  const problem = catalogue.error instanceof ApiProblemError ? catalogue.error.problem : undefined
  const canProceed = config.singleRef || effectiveChecked.size > 0

  return (
    <div data-scr={brandUnitId(config.unitIds.targets)} data-cmp="pane">
      <WizardSplit>
        <div>
          <WizardHeading>{heading}</WizardHeading>
          <WizardProse>This restore writes into the workspace you choose below — the same one it was captured from by default.</WizardProse>
        </div>

        <WizardCard>
          <h4 className="m-0 mb-4 text-body-lg font-semibold">Destination and scope</h4>

          <Field label="Destination">
            <DestinationWorkspacePicker currentWorkspaceId={workspaceId} value={destination.id} onChange={(id, name) => setDestination({ id, name })} />
          </Field>

          <Field label="Recovering">
            <div data-cmp="field-control">
              {config.singleRef ? (
                <Chip>{config.kindLabel}</Chip>
              ) : (
                <Chip>
                  {effectiveChecked.size} of {kindEntry?.objects.length ?? 0} object types selected
                </Chip>
              )}
            </div>
          </Field>

          <WizardButtons>
            <Button data-act={actionId(`nav.${config.singleRef ? config.unitIds.selectRef! : config.unitIds.select}`)} ghost onClick={onBack}>
              Back
            </Button>
            <Button
              data-act={actionId(`nav.${config.unitIds.plan}`)}
              disabled={!canProceed}
              onClick={() =>
                onNext({
                  objectTypes: config.singleRef ? undefined : [...effectiveChecked],
                  destinationWorkspaceId: destination.id !== workspaceId ? destination.id : undefined,
                  destinationLabel: destination.name,
                })
              }
            >
              Next
            </Button>
          </WizardButtons>
        </WizardCard>
      </WizardSplit>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      {/* A catalogue with at most one object type has nothing to choose between — `repo-wz-2`'s
       * own spec states this in words ("No table on this pane"): a single-row checklist would let
       * an operator uncheck the only thing this machine ever restores, which is not a real choice. */}
      {!config.singleRef && (kindEntry?.objects.length ?? 0) > 1 && (
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2 mt-6">
          <div data-cmp="panel-header" className="flex items-center gap-5 p-5 border-b border-bd font-semibold text-body-lg">
            <span className="flex-1">What to recover</span>
            <div data-cmp="grid-tools">
              <IconButton
                data-act={undefined}
                icon="i-refresh"
                label="Refresh"
                disabled={catalogue.isFetching}
                onClick={() => queryClient.invalidateQueries({ queryKey: ['restore-object-catalogue', orgId] })}
              />
            </div>
          </div>
          <GridTable
            columns={[
              {
                key: 'pick',
                header: '',
                render: (o) => (
                  <input
                    type="checkbox"
                    disabled={o.not_restorable}
                    checked={effectiveChecked.has(o.object_type)}
                    onChange={(e) => {
                      const next = new Set(effectiveChecked)
                      if (e.target.checked) next.add(o.object_type)
                      else next.delete(o.object_type)
                      setChecked(next)
                    }}
                  />
                ),
              },
              {
                key: 'label',
                header: '',
                render: (o) => (
                  <>
                    <b>{o.label}</b>
                    <p className="m-0 text-body text-muted">{o.recovers}</p>
                    <p className="m-0 text-small text-muted">Requires: {o.prerequisite}</p>
                  </>
                ),
              },
              { key: 'fidelity', header: '', render: (o) => <Chip variant={o.not_restorable ? 'failure' : o.fidelity === 'Full' ? 'active' : 'warning'}>{o.fidelity}</Chip> },
            ]}
            rows={kindEntry?.objects ?? []}
            getRowKey={(o) => o.object_type}
            showHeader={false}
          />
          <div data-cmp="grid-footer" className="border-t border-bd p-5">
            <Note>Every object type this machine can restore — not paged, not filtered.</Note>
          </div>
        </div>
      )}
    </div>
  )
}
